import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';

import { catchError, throwError } from 'rxjs';
import { AttributeType } from '../../enums';
import { Field, FormValues, RequiredField, SelectedUnits } from '../../models';
import { DynamicFormBaseService } from '../../services/dynamic-form-base.service';
import { NotificationService, NotificationType } from '../../services/notification.service';
import { ProductsService } from '../../services/products.service';
import { UnitConversionService } from '../../services/unit-conversion.service';

export interface OptionItem {
  value: number | string;
  label: string;
}

@Component({
  selector: 'app-dynamic-form-model',
  templateUrl: './dynamic-form-model.component.html',
  styleUrls: ['./dynamic-form-model.component.scss'],
  standalone: false,
})
export class DynamicFormModelComponent implements OnInit, OnChanges {
  private readonly FIELD_NAMES = {
    DESICCANT_DEW_POINT: 'desiccantDewPoint',
    SELECTED_MODEL: 'selectedModel',
  } as const;

  private readonly MODEL_TYPES = {
    HEATED: 'heated',
    IBA_SUFFIX: 'IBA',
  } as const;

  private readonly DEFAULT_VALUES = {
    DESICCANT_MINUS_40: '-40',
    DESICCANT_MINUS_70: '-70',
  } as const;

  public FormInputs = AttributeType;
  @Input() formFields!: Field[];
  @Input() tab!: string;
  @Input() models: string[] = [];
  @Input() defaultModelName!: string;
  @Output() formValueChanged = new EventEmitter<{
    formValues: FormValues;
    requiredFields: RequiredField[];
    selectedUnits: SelectedUnits;
  }>();
  @Output() modelSelected = new EventEmitter<string>();

  dynamicForm!: FormGroup;
  showWarning: string | boolean = false;
  selectedUnits: { [key: string]: string } = {};
  convertedMinMax: { [key: string]: { min: number; max: number } } = {};
  originalMinMax: { [key: string]: { min: number; max: number } } = {};
  filteredOptions: { [key: string]: string[] } = {};
  originalOptions: { [key: string]: string[] } = {};

  constructor(
    private productService: ProductsService,
    private unitConversionservice: UnitConversionService,
    private notificationService: NotificationService,
    private formBaseService: DynamicFormBaseService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['formFields'] && !changes['formFields'].isFirstChange()) {
      this.initializeForm();
    }
    if (changes['tab'] && !changes['tab'].isFirstChange()) {
      this.patchFormValues();
    }
  }

  ngOnInit(): void {
    this.initializeForm();
  }

  private initializeForm(): void {
    this.productService.initializeModelFormValues();

    const group = this.createFormGroup();
    this.dynamicForm = new FormGroup(group);
    this.setupFormSubscription();
    this.patchFormValues();
    this.cdr.markForCheck();
  }

  private createFormGroup(): { [key: string]: FormControl } {
    const group = {
      selectedModel: new FormControl('', Validators.required),
    };
    if (!this.formFields?.length) {
      return group;
    }
    const storedFieldValues = this.productService.getFormFieldValuesModel();
    this.formFields.forEach((field: Field) => {
      const validators = this.formBaseService.buildValidators(field);
      const initialValue = storedFieldValues[field.name] !== undefined ? storedFieldValues[field.name] : field?.value;
      group[field.name] = new FormControl(initialValue, validators);
      this.formBaseService.initializeUnitIfNeeded(field, this.selectedUnits);
      this.formBaseService.storeOriginalMinMax(field, this.originalMinMax);

      // Store original options for dropdown fields
      if (field.type === 'dropdown' && field.options) {
        this.originalOptions[field.name] = [...field.options];
        this.filteredOptions[field.name] = [...field.options];
      }
    });
    return group;
  }

  private setupFormSubscription(): void {
    this.dynamicForm.valueChanges
      .pipe(
        catchError((error) => {
          this.notificationService.showNotification(NotificationType.Error, 'An error occurred during value changes in field.');
          return throwError(error);
        }),
      )
      .subscribe((res) => this.handleFormValueChanges(res));
  }

  private handleFormValueChanges(formValues: FormValues): void {
    this.productService.updateFormFieldValuesModel(this.dynamicForm.value);

    // Apply desiccant dew point filter when selectedModel changes
    const selectedModel = this.dynamicForm.get('selectedModel')?.value;
    if (selectedModel) {
      this.applyDesiccantDewPointFilter(selectedModel);
    }

    const requiredFields = this.getRequiredFields();
    this.formBaseService.convertFormValuesToBaseUnits(formValues, this.formFields, this.selectedUnits).then((convertedValues) => {
      this.updateFormState(formValues);
      this.formBaseService.checkTemperatures(formValues, this.selectedUnits, this.formFields).subscribe((warningMsg) => {
        this.showWarning = warningMsg;
        this.emitFormChanges(convertedValues, requiredFields);
      });
    });
    this.modelSelected.emit(this.dynamicForm.get('selectedModel')?.value);
  }

  private applyDesiccantDewPointFilter(selectedModel: string): void {
    if (!this.isValidModel(selectedModel)) {
      return;
    }

    const desiccantField = this.getDesiccantField();
    if (!desiccantField) {
      return;
    }

    const isRestricted = this.isRestrictedModel(selectedModel);
    this.updateDesiccantOptions(isRestricted);
    this.refreshUI();
  }

  private isValidModel(selectedModel: string): boolean {
    return selectedModel !== null && selectedModel !== undefined && selectedModel.trim().length > 0;
  }

  private getDesiccantField(): Field | undefined {
    return this.formFields.find((f) => f.name === this.FIELD_NAMES.DESICCANT_DEW_POINT);
  }

  private isRestrictedModel(selectedModel: string): boolean {
    return (
      selectedModel.toLowerCase().includes(this.MODEL_TYPES.HEATED) || selectedModel.toUpperCase().endsWith(this.MODEL_TYPES.IBA_SUFFIX)
    );
  }

  private getFilteredDesiccantOptions(isRestricted: boolean): string[] {
    const originalDesiccantOptions = this.originalOptions[this.FIELD_NAMES.DESICCANT_DEW_POINT];

    if (!originalDesiccantOptions || originalDesiccantOptions.length === 0) {
      return [this.DEFAULT_VALUES.DESICCANT_MINUS_40];
    }

    if (isRestricted) {
      return originalDesiccantOptions.filter((desiccantOption) => this.isMinus40Value(desiccantOption));
    }

    return [...originalDesiccantOptions];
  }

  private isMinus40Value(desiccantOption: string | number): boolean {
    return String(desiccantOption).trim() === this.DEFAULT_VALUES.DESICCANT_MINUS_40;
  }

  private setDesiccantValue(value: string): void {
    const desiccantControl = this.dynamicForm.get(this.FIELD_NAMES.DESICCANT_DEW_POINT);

    if (desiccantControl && desiccantControl.value !== value) {
      desiccantControl.setValue(value, { emitEvent: false });
    }
  }

  private updateDesiccantOptions(isRestricted: boolean): void {
    const filteredDesiccantOptions = this.getFilteredDesiccantOptions(isRestricted);
    this.filteredOptions[this.FIELD_NAMES.DESICCANT_DEW_POINT] = filteredDesiccantOptions;

    if (isRestricted) {
      this.setDesiccantValue(this.DEFAULT_VALUES.DESICCANT_MINUS_40);
    }
  }

  private refreshUI(): void {
    this.cdr.detectChanges();
  }

  private getRequiredFields(): RequiredField[] {
    return this.formFields
      .filter((field: Field) => field.required)
      .map((field: Field) => ({
        name: field.name,
        value: this.dynamicForm.get(field.name)?.value,
        min: this.originalMinMax[field.name]?.min,
        max: this.originalMinMax[field.name]?.max,
      }));
  }

  private updateFormState(formValues: FormValues): void {
    const combinedState = {
      formValues: formValues,
      selectedUnits: this.selectedUnits,
    };
    this.productService.setFormStateModel(combinedState);
  }

  private emitFormChanges(convertedValues: FormValues, requiredFields: RequiredField[]): void {
    this.formValueChanged.emit({
      formValues: convertedValues,
      requiredFields: requiredFields,
      selectedUnits: this.selectedUnits,
    });
  }

  onUnitChange(event: Event, field: Field): void {
    this.formBaseService.onUnitChange(event, field, this.dynamicForm, this.selectedUnits, this.originalMinMax, this.convertedMinMax);
  }

  onInputChange(event: Event, field: Field) {
    const input = event.target as HTMLInputElement;
    const value = input.value;
    // Only convert to number when the field loses focus
    if (value && !isNaN(parseFloat(value))) {
      this.dynamicForm.get(field.name)?.setValue(parseFloat(value));
    }
  }

  private patchFormValues(): void {
    const savedValues = this.productService.getFormStateModel();
    const savedConditionValues = this.productService.getFormStateCondition();
    const storedFieldValues = this.productService.getFormFieldValuesModel();
    if (Object.keys(storedFieldValues).length > 0) {
      this.dynamicForm.patchValue(storedFieldValues);
    }
    if (savedConditionValues && savedValues === null) {
      const { formValues, selectedUnits } = savedConditionValues;
      this.dynamicForm.patchValue(formValues);
      this.selectedUnits = { ...selectedUnits };
    } else if (savedValues != savedConditionValues) {
      const { formValues, selectedUnits } = savedValues;
      this.dynamicForm.patchValue(formValues);
      this.selectedUnits = { ...selectedUnits };
    }
    this.formFields.forEach((field: Field) => {
      if (this.originalMinMax[field.name] && field.baseUnit && this.selectedUnits[field.name] && field.category) {
        field.min = this.originalMinMax[field.name]?.min;
        field.max = this.originalMinMax[field.name]?.max;
        this.convertMinMax(field, field.baseUnit, this.selectedUnits[field.name], field.category);
        this.updateValidators(field.name, field.min, field.max);
      }
    });
  }

  private convertMinMax(field: Field, baseUnit: string, newUnit: string, valueType: string): void {
    this.formBaseService.convertMinMax(field, baseUnit, newUnit, valueType, this.originalMinMax, this.convertedMinMax, this.dynamicForm);
  }

  private updateValidators(fieldName: string, min: number, max: number): void {
    this.formBaseService.updateValidators(fieldName, min, max, this.dynamicForm);
  }
}
